import { useMemo } from "react";
import type * as GeoJSON from "geojson";

import { useProjectById } from "../../api/project";
import { useProjects } from "../../api/projects";
import { useProjectFolders } from "../../api/projectFolders";
import type { Project, ProjectBoundary } from "../../types/project";
import type { PublicStory, StoryContent, StoryMap, StoryScope } from "../../types/storyMap";
import { buildFolderIndex, descendantIds, normalizeFolderId } from "../projectListing/folderTree";
import { type Bbox, bboxOfProjects, bboxPolygon, projectBbox, unionBbox } from "../projectListing/projectBbox";

/** A project a story may draw from, as the builder's pickers list it. */
export interface ScopeProject {
  id: number;
  name: string;
  bbox: Bbox | null;
  boundary: ProjectBoundary | null;
  folder: number | null;
  operationalStrategyId: number | null;
}

/**
 * Which projects a story may cite, resolved once per page.
 *
 * A project story has a universe of one and `defaultProjectId` set: its
 * entries carry no projectId. A folder story's universe is every active
 * project in the folder's subtree, an organisation-wide story's every active
 * project; their entries name the project they draw from.
 */
export interface StoryScopeInfo {
  kind: StoryScope;
  /** Project name, folder name, or "All projects". */
  name: string;
  projectId: number | null;
  folderId: number | null;
  universe: ScopeProject[];
  byId: ReadonlyMap<number, ScopeProject>;
  /** What the camera frames when a scene has none: the universe's extent. */
  extent: Bbox | null;
  extentFeature: GeoJSON.Feature | null;
  /** Project stories: the project every entry implicitly cites. */
  defaultProjectId: number | null;
  /** Folder and organisation-wide stories: entries must say which project. */
  isScoped: boolean;
  loading: boolean;
}

export const ROOT_SCOPE_NAME = "All projects";

const EMPTY_UNIVERSE: ScopeProject[] = [];
const EMPTY_BY_ID: ReadonlyMap<number, ScopeProject> = new Map();

export const EMPTY_SCOPE: StoryScopeInfo = {
  kind: "project",
  name: "",
  projectId: null,
  folderId: null,
  universe: EMPTY_UNIVERSE,
  byId: EMPTY_BY_ID,
  extent: null,
  extentFeature: null,
  defaultProjectId: null,
  isScoped: false,
  loading: true,
};

function scopeProjectOf(project: Project): ScopeProject {
  return {
    id: Number(project.id),
    name: project.name,
    bbox: projectBbox(project),
    boundary: project.boundary ?? null,
    folder: project.folder ?? null,
    operationalStrategyId: project.operational_strategy ?? null,
  };
}

/** "North project" / "Coastal folder" / "All projects" — the byline fragment. */
export function scopeByline(kind: StoryScope | undefined, name: string | null | undefined): string | null {
  if (kind === "root") return ROOT_SCOPE_NAME;
  if (!name) return null;
  return kind === "folder" ? `${name} folder` : `${name} project`;
}

/** The scope a frozen story describes, for anonymous readers (names only). */
export function scopeFromContent(content: StoryContent | null | undefined, publicStory?: PublicStory | null): StoryScopeInfo {
  const kind: StoryScope = publicStory?.scope ?? content?.scope ?? (content?.projects?.length ? "folder" : "project");
  const name = publicStory?.scope_name ?? content?.scopeName ?? content?.projectName ?? publicStory?.project_name ?? "";
  const universe: ScopeProject[] = (content?.projects ?? []).map((p) => ({
    id: Number(p.id),
    name: p.name,
    bbox: null,
    boundary: null,
    folder: null,
    operationalStrategyId: null,
  }));
  return {
    kind,
    name,
    projectId: null,
    folderId: null,
    universe,
    byId: new Map(universe.map((p) => [p.id, p])),
    extent: null,
    extentFeature: null,
    defaultProjectId: null,
    isScoped: kind !== "project",
    loading: false,
  };
}

/**
 * The scope of a story being authored or previewed (authed). A project
 * story loads its one project; folder and organisation-wide stories read
 * the project and folder lists (cached from the listing page).
 */
export function useStoryScope(story: StoryMap | null | undefined): StoryScopeInfo {
  const kind: StoryScope | undefined = story?.scope;
  const scoped = !!story && kind !== undefined && kind !== "project";
  const projectQuery = useProjectById(kind === "project" ? story?.project_id : null);
  const { projects, isLoading: projectsLoading } = useProjects(undefined, undefined, { enabled: scoped });
  const { folders, isLoading: foldersLoading } = useProjectFolders(scoped);

  return useMemo<StoryScopeInfo>(() => {
    if (!story) return EMPTY_SCOPE;
    if (!scoped) {
      const project = projectQuery.data?.data;
      const universe = project ? [scopeProjectOf(project)] : EMPTY_UNIVERSE;
      const extent = universe[0]?.bbox ?? null;
      return {
        kind: "project",
        name: project?.name ?? story.project_name ?? "",
        projectId: story.project_id,
        folderId: null,
        universe,
        byId: new Map(universe.map((p) => [p.id, p])),
        extent,
        extentFeature: extent ? bboxPolygon(extent) : null,
        defaultProjectId: story.project_id,
        isScoped: false,
        loading: projectQuery.isPending,
      };
    }
    const all = (projects?.data ?? []).filter((p) => !p.is_archived);
    const index = buildFolderIndex(folders);
    let members: Project[];
    let name: string;
    if (kind === "folder" && story.folder_id !== null) {
      const ids = descendantIds(index, story.folder_id);
      ids.add(story.folder_id);
      members = all.filter((p) => {
        const f = normalizeFolderId(index, p.folder);
        return f !== null && ids.has(f);
      });
      name = index.byId.get(story.folder_id)?.name ?? "Folder";
    } else {
      members = all;
      name = ROOT_SCOPE_NAME;
    }
    const universe = members.map(scopeProjectOf).sort((a, b) => a.name.localeCompare(b.name));
    const folderBbox = story.folder_id !== null ? index.byId.get(story.folder_id)?.boundary_bbox ?? null : null;
    const extent = unionBbox([bboxOfProjects(members), folderBbox]);
    return {
      kind: kind ?? "root",
      name,
      projectId: null,
      folderId: story.folder_id,
      universe,
      byId: new Map(universe.map((p) => [p.id, p])),
      extent,
      extentFeature: extent ? bboxPolygon(extent) : null,
      defaultProjectId: null,
      isScoped: true,
      loading: projectsLoading || foldersLoading,
    };
  }, [story, scoped, kind, projectQuery.data, projectQuery.isPending, projects, folders, projectsLoading, foldersLoading]);
}

/** Project id → name, for legends and chips. */
export function projectNamesOf(scope: StoryScopeInfo): Record<string, string> {
  const out: Record<string, string> = {};
  scope.universe.forEach((p) => {
    out[String(p.id)] = p.name;
  });
  return out;
}
